import useTranslation from 'next-translate/useTranslation';
import React, { FunctionComponent } from 'react';
import { FieldValues, useFormContext, UseFormRegister } from 'react-hook-form';

import {
  Phone,
  Country,
  CountryShipping,
  Text,
} from '../../ui/inputs/generic-input';
import TextInput from '../../ui/v2/inputs/text';

export const PersonalData: FunctionComponent<{
  register: UseFormRegister<FieldValues>;
  errors: Record<string, any>;
  isDisabled: boolean;
  prefix?: string;
  showPassword?: boolean;
}> = ({ register, errors, isDisabled, prefix = 'id', showPassword }) => {
  const { t } = useTranslation();
  return (
    <div className="grid lg:grid-flow-col gap-4 lg:gap-6 items-start">
      <div>
        <Text
          {...register('name', { required: t('forms:errors.required') })}
          label={t('forms:user.name.label')}
          id={`${prefix}-name`}
          placeholder={t('forms:user.name.placeholder')}
          disabled={isDisabled}
          error={errors?.name?.message}
        />
      </div>
      <Text
        {...register('email', {
          required: t('forms:errors.required'),
          maxLength: {
            value: 150,
            message: t('forms:errors.maxLenght', { value: 150 }),
          },
          pattern: {
            value: /^\S+@\S+$/i,
            message: t('forms:errors.invalidEmail'),
          },
        })}
        label={t('forms:user.email.label')}
        type="email"
        id={`${prefix}-email`}
        placeholder={t('forms:user.email.placeholder')}
        disabled={isDisabled}
        error={errors?.email?.message}
      />
      {showPassword && (
        <Text
          {...register('password', {
            required: t('forms:errors.required'),
            minLength: {
              value: 6,
              message: t('forms:errors.minLenght', { value: 6 }),
            },
          })}
          label={t('forms:user.password.label')}
          type={'password'}
          id="newPassowrd"
          placeholder={t('forms:password.new_password.placeholder')}
          error={errors?.password?.message}
        />
      )}
    </div >
  );
};

export const AddressData: FunctionComponent<{
  register: UseFormRegister<FieldValues>;
  isDisabled: boolean;
  readOnly?: boolean;
  errors?: Record<string, any>;
  prefix?: string;
  isBilling: boolean;
}> = ({
  register,
  isDisabled,
  readOnly = false,
  errors,
  isBilling,
  prefix = 'id',
}) => {
    const { t } = useTranslation();

    return (
      <>
        <div className="-mx-4 lg:mx-0">
          <Text
            {...register('description', { required: t('forms:errors.required') })}
            label={t('forms:addresses.description.label')}
            id={`${prefix}-description`}
            placeholder={t('forms:addresses.description.placeholder')}
            disabled={readOnly ? true : isDisabled}
            error={errors?.description?.message}
          />
        </div>
        <div className="-mx-4 lg:mx-0">
          <Text
            {...register('name', { required: t('forms:errors.required') })}
            label={t('forms:addresses.name.label')}
            id={`${prefix}-name`}
            placeholder={t('forms:addresses.name.placeholder')}
            disabled={readOnly ? true : isDisabled}
            error={errors?.name?.message}
          />
        </div>
        <div className="lg:col-span-2 -mx-4 lg:mx-0">
          <Text
            {...register('street', { required: t('forms:errors.required') })}
            label={t('forms:addresses.address.label')}
            id={`${prefix}-address`}
            placeholder={t('forms:addresses.address.placeholder')}
            disabled={readOnly ? true : isDisabled}
            error={errors?.street?.message}
          />
        </div>
        <div className="-mx-4 lg:mx-0">
          <Text
            {...register('zip_code', { required: t('forms:errors.required') })}
            label={t('forms:addresses.postal_code.label')}
            id={`${prefix}-postal-code`}
            placeholder={t('forms:addresses.postal_code.placeholder')}
            disabled={readOnly ? true : isDisabled}
            error={errors?.zip_code?.message}
          />
        </div>
        <div className="-mx-4 lg:mx-0">
          <Text
            {...register('city', { required: t('forms:errors.required') })}
            label={t('forms:addresses.city.label')}
            id={`${prefix}-city`}
            placeholder={t('forms:addresses.city.placeholder')}
            disabled={readOnly ? true : isDisabled}
            error={errors?.city?.message}
          />
        </div>
        <div className="grid lg:grid-cols-3 gap-4 lg:gap-6 lg:col-span-2 items-start -mx-4 lg:mx-0">
          <div>
            {isBilling ? (
              <Country
                register={register('country', {
                  required: t('forms:errors.required'),
                })}
                label={t('forms:addresses.country.label')}
                placeholder={t('forms:addresses.country.placeholder')}
                id={`${prefix}-country`}
                disabled={readOnly ? true : isDisabled}
                error={errors?.country?.message}
              />
            ) : (
              <CountryShipping
                register={register('country_shipping', {
                  required: t('forms:errors.required'),
                })}
                label={t('forms:addresses.country.label')}
                placeholder={t('forms:addresses.country.placeholder')}
                id={`${prefix}-country-shipping`}
                disabled={readOnly ? true : isDisabled}
                error={
                  errors?.country_shipping
                    ? errors?.country_shipping.message
                    : errors?.country?.message &&
                      errors?.shipping_destination?.message
                      ? errors?.country?.message +
                      ', ' +
                      errors?.shipping_destination?.message
                      : errors?.country?.message
                        ? errors?.country?.message
                        : errors?.shipping_destination?.message
                }
              />
            )}
          </div>
          <div className="lg:col-span-2">
            <Phone
              register={register}
              prefix={`${prefix}-phone`}
              disabled={isDisabled}
              errors={errors?.phone}
            />
          </div>
        </div>
        {isBilling && (
          <span className="lg:grid lg:col-span-3 lg:grid-cols-3 gap-6 items-start -mx-4 lg:mx-0">
            <span className="lg:col-span-1">
              <Text
                {...register('vat_number')}
                label={t('forms:addresses.vat.label')}
                id={`${prefix}-vat`}
                placeholder={t('forms:addresses.vat.placeholder')}
                disabled={readOnly ? true : isDisabled}
                error={errors?.vat_number?.message}
                required={false}
              />
            </span>
          </span>
        )}
      </>
    );
  };

export const ContactData: FunctionComponent<{
  register: UseFormRegister<FieldValues>;
  isDisabled: boolean;
  errors?: Record<string, any>;
  prefix?: string;
}> = ({ register, isDisabled, errors, prefix = 'id' }) => {
  const { t } = useTranslation();
  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <Text
        {...register('description', { required: t('forms:errors.required') })}
        label={t('forms:contact.name.label')}
        id={`${prefix}-description`}
        placeholder={t('forms:contact.name.placeholder')}
        disabled={isDisabled}
        error={errors?.description?.message}
      />
      <Phone
        register={register}
        prefix={`${prefix}-phone`}
        disabled={isDisabled}
        errors={errors?.phone}
      />
    </div>
  );
};

export const UserData: FunctionComponent<{
  isDisabled: boolean;
  prefix?: string;
  showStatus?: boolean;
  statusGreen?: boolean;
}> = ({
  isDisabled,
  prefix = 'id',
  showStatus = true,
  statusGreen = true,
}) => {
    const { setValue, register, clearErrors, watch, resetField, formState: { errors } } = useFormContext();
    const { t } = useTranslation();
    return (
      <div className="grid lg:grid-flow-col gap-6 items-start">
        <div>
          <TextInput
            {...register('name', { required: t('forms:errors.required') })}
            label={t('forms:user.name.label')}
            id={`${prefix}-name`}
            placeholder={t('forms:user.name.placeholder')}
            disabled={isDisabled}
            error_messages={errors?.name?.message ? [errors?.name?.message] : []}
            labelBehaviour={'animated'}
            border
            clearButton
          />
        </div>
        <div>
          <TextInput
            {...register('email', {
              required: t('forms:errors.required'),
              maxLength: {
                value: 150,
                message: t('forms:errors.maxLenght', { value: 150 }),
              },
              pattern: {
                value: /^\S+@\S+$/i,
                message: t('forms:errors.invalidEmail'),
              },
            })}
            label={t('forms:user.email.label')}
            type="email"
            id={`${prefix}-email`}
            placeholder={t('forms:user.email.placeholder')}
            disabled={isDisabled}
            error_messages={errors?.name?.message ? [errors?.name?.message] : []}
            labelBehaviour={'animated'}
            border
            clearButton
          />
        </div>
        {showStatus && (
          <div>
            <TextInput
              {...register('status')}
              label={t('forms:user.status.label')}
              id={`${prefix}-status`}
              readOnly={true}
              labelBehaviour={'animated'}
              border
              clearButton
            />
          </div>
        )}
      </div>
    );
  };
